// The fare break-up, as rows (2026-09-27).
//
// One line per charge, in the order the engine adds them, so the column adds
// down to each bold row. The engine's own `subtotal` is deliberately not
// shown: it means "the rate card's figures before the garage leg", which is
// not what a reader takes "subtotal" to mean, and printing it beside the
// charges invites exactly that misreading. What a duty slip needs is the
// amount GST was charged on, which is a different number.
//
// Every row is rendered even at zero — a zero must read as "checked, did not
// apply", never as "forgotten" — so each carries a `why` explaining itself.
//
// Nothing here computes a fare. Every figure comes from the backend's
// preview; the only arithmetic is summing lines the engine already returned,
// to show the tax base it used.

const n = (v) => Number(v) || 0;
const km = (v) => `${(Math.round(n(v) * 100) / 100).toLocaleString('en-IN')} km`;
const rupees = (v) => `₹${(Math.round(n(v) * 100) / 100).toLocaleString('en-IN')}`;

const line = (charge) => ({
  key: charge.chargeType,
  label: charge?.meta?.name || charge.chargeType,
  amount: n(charge.addedCharge),
});

const findCharge = (breakdown, ...types) =>
  (breakdown || []).find((b) => types.includes(b?.chargeType)) || null;

// Why an allowance came to what it did. Airport and rental judge it against a
// window the company configures and the engine reports `applied`; outstation
// instead counts the nights the trip stayed away and reports `noOfNights`, so
// the two cases need different sentences — telling an operator no window is
// configured would be wrong on an outstation trip, where none is meant to be.
const windowWhy = (charge) => {
  const meta = charge?.meta || {};
  const rate = n(meta.rate);
  const { windowStart, windowEnd, applied, noOfNights } = meta;
  if (!rate) return 'not set on this rate card';
  if (noOfNights !== undefined) {
    const nights = n(noOfNights);
    return nights
      ? `${rupees(rate)} × ${nights} ${nights === 1 ? 'night' : 'nights'} away`
      : `${rupees(rate)} per night · the trip stayed no nights away`;
  }
  if (!windowStart || !windowEnd) return `${rupees(rate)} · no window configured for this company`;
  const window = `${windowStart}–${windowEnd}`;
  return applied
    ? `${rupees(rate)} · trip started within ${window}`
    : `${rupees(rate)} · trip started outside ${window}`;
};

// The rate card's own charge, in the terms the card is written in. fareBasis
// comes from the engine; without it we can only print the fused figure.
const basisRows = (pricing) => {
  const basis = pricing?.fareBasis;
  const fused = n(pricing?.baseFareDisplay);

  if (!basis) {
    return [{ key: 'baseFare', label: 'Trip Charge', amount: fused, why: 'as priced' }];
  }

  if (basis.kind === 'perKm') {
    return [
      {
        key: 'baseFare',
        label: 'Distance Charge',
        amount: fused,
        why: `${km(basis.distance)} × ${rupees(basis.perKm)}`,
      },
    ];
  }

  if (basis.kind === 'flatRate' || basis.kind === 'flatPlusKm') {
    const rows = [
      {
        key: 'flatAmount',
        label: 'Flat Rate',
        amount: n(basis.flatAmount),
        why:
          basis.kind === 'flatPlusKm' ? `covers the first ${km(basis.includedKm)}` : 'fixed fare',
      },
    ];
    if (basis.kind === 'flatPlusKm') {
      rows.push({
        key: 'flatExtraKm',
        label: 'Extra KM',
        amount: n(basis.extraKmFare),
        why:
          n(basis.extraKmDistance) > 0
            ? `${km(basis.extraKmDistance)} beyond ${km(basis.includedKm)} × ${rupees(basis.perKm)}`
            : `${km(basis.distance)} run, within the ${km(basis.includedKm)} included`,
      });
    }
    return rows;
  }

  if (basis.kind === 'package') {
    const perDay = String(basis.durationType || '').toLowerCase() === 'day';
    const days = n(basis.totalDays) || 1;
    return [
      {
        key: 'package',
        label: 'Package',
        amount: fused,
        why: perDay
          ? `${rupees(basis.cost)} × ${days} ${days === 1 ? 'day' : 'days'}, ${km(basis.totalAllowedKm)} allowed`
          : `${rupees(basis.cost)} for ${basis.duration} hr, ${km(basis.totalAllowedKm)} allowed`,
      },
    ];
  }

  return [{ key: 'baseFare', label: 'Trip Charge', amount: fused, why: 'as priced' }];
};

// Overages the engine priced as modifiers. Rentals and outstation have both;
// airport cards price their extra kilometres inside the basis instead.
const overageRows = (pricing) => {
  const rows = [];
  const extraKm = findCharge(pricing?.breakdown, 'extraKm', 'extraKM');
  const extraHrs = findCharge(pricing?.breakdown, 'extraHrs');

  // The engine reports the overage as a signed figure: negative means the
  // trip stayed inside the allowance (and its grace) by that much, so only a
  // positive one has been charged for.
  if (extraKm) {
    const meta = extraKm.meta || {};
    const over = n(meta?.extraKms?.roundedOff ?? meta?.extraKms?.actualValue);
    const grace = n(meta?.graceValue);
    rows.push({
      ...line(extraKm),
      key: 'extraKm',
      label: 'Extra KM',
      why:
        over > 0
          ? `${km(over)} beyond ${km(meta.totalAllowedKm)} × ${rupees(meta.rate)}`
          : `within the ${km(meta.totalAllowedKm)} allowed${grace ? ` and its ${km(grace)} grace` : ''}`,
    });
  }
  if (extraHrs) {
    const meta = extraHrs.meta || {};
    const over = n(meta?.extraHrs?.roundedOff ?? meta?.extraHrs?.actualValue);
    const grace = n(meta?.graceValue);
    // Outstation does not charge overtime by the hour: hours past the package
    // become whole extra days, counted in the package row above. The engine
    // says so in `info`, and repeating "within the package hours" here would
    // imply an hourly line that this duty type never has.
    rows.push({
      ...line(extraHrs),
      key: 'extraHrs',
      label: 'Extra Hours',
      why: meta.info
        ? 'charged as extra days, counted in the package above'
        : over > 0
          ? `${over} hr beyond the package × ${rupees(meta.rate)}`
          : `within the package hours${grace ? ` and their ${grace} hr grace` : ''}`,
    });
  }
  return rows;
};

// Charges the engine adds after the rate card's own figures, all of them
// taxable. Garage is always shown; the rest only when the trip has them or
// the rate card configures them, so the list stays about this trip.
const addedRows = (pricing) => {
  const rows = [];
  const breakdown = pricing?.breakdown || [];

  const garage = findCharge(breakdown, 'garageCharge');
  if (garage) {
    const legs = garage?.meta?.breakup || [];
    const pickup = legs.find((l) => l?.name === 'GarageToPickup');
    const drop = legs.find((l) => l?.name === 'DropToGarage');
    rows.push({
      ...line(garage),
      key: 'garageCharge',
      label: 'Garage Charge',
      why:
        pickup || drop
          ? `to pickup ${rupees(pickup?.totalCharge)} + drop back ${rupees(drop?.totalCharge)}`
          : 'garage legs',
    });
  }

  ['nightAllowance', 'earlyStartAllowance'].forEach((type) => {
    const charge = findCharge(breakdown, type);
    if (charge) rows.push({ ...line(charge), why: windowWhy(charge) });
  });

  const driver = findCharge(breakdown, 'driverAllowance');
  if (driver && (n(driver.addedCharge) || n(driver?.meta?.rate))) {
    rows.push({
      ...line(driver),
      why: n(driver?.meta?.rate) ? `${rupees(driver.meta.rate)} per day` : 'as configured',
    });
  }

  // "Additional charges" is a container, not a charge: the rate card names what
  // is inside it — Advance Booking, and whatever else a company configures — and
  // the engine reports each by name in meta.appliedCharges. A reader comparing
  // this row against the rate card needs the name they typed the figure under,
  // so the row carries it; several become the row's reason.
  const extras = findCharge(breakdown, 'additionalCharges');
  if (extras && n(extras.addedCharge)) {
    const applied = (extras?.meta?.appliedCharges || []).filter((c) => !c?.skipped);
    const names = applied.map((c) => c?.name || c?.label).filter(Boolean);
    rows.push({
      ...line(extras),
      key: 'additionalCharges',
      label: names.length === 1 ? names[0] : 'Additional Charges',
      why: names.length > 1 ? names.join(' + ') : 'on the rate card',
    });
  }

  ['waitingTimeCharge', 'placard'].forEach((type) => {
    const charge = findCharge(breakdown, type);
    if (charge && n(charge.addedCharge)) rows.push({ ...line(charge), why: 'as recorded' });
  });

  const coupon = findCharge(breakdown, 'discountAmount');
  if (coupon && n(coupon.addedCharge)) {
    rows.push({ ...line(coupon), key: 'discountAmount', why: 'applied to this trip' });
  }

  return rows;
};

// Tolls and parking are reimbursements, not service: the engine adds them
// after GST and they are not part of the tax base.
const reimbursementRows = (pricing) => {
  const breakdown = pricing?.breakdown || [];
  return ['tollCharges', 'parkingCharge']
    .map((type) => findCharge(breakdown, type))
    .filter(Boolean)
    .map((charge) => ({ ...line(charge), why: 'reimbursed, not taxed' }));
};

const gstWhy = (charge) => {
  const parts = charge?.meta?.breakup || [];
  if (!parts.length) return 'on the taxable amount';
  return `${parts.map((p) => p?.label || p?.name).join(' + ')}, on the taxable amount`;
};

/**
 * The break-up as sections, for any duty type. Returns null before the first
 * preview lands, so the caller can say so rather than render an empty frame.
 */
export const fareRows = (pricing) => {
  if (!pricing) return null;

  const charged = [...basisRows(pricing), ...overageRows(pricing), ...addedRows(pricing)];

  // The rate card's outer columns sit with the charges they belong to.
  const surCharge = n(pricing.surCharge);
  const platformCharge = n(pricing.platformCharge);
  const baseRate = n(pricing.baseRate);
  const outer = [];
  if (baseRate)
    outer.push({ key: 'baseRate', label: 'Base Fare', amount: baseRate, why: 'on the rate card' });
  outer.push({ key: 'surCharge', label: 'Surcharge', amount: surCharge, why: 'on the rate card' });
  outer.push({
    key: 'platformCharge',
    label: 'Platform Charge',
    amount: platformCharge,
    why: 'on the rate card',
  });

  // Insert the card's outer columns after its own figures, before the
  // charges the engine adds on top.
  const basisCount = basisRows(pricing).length + overageRows(pricing).length;
  const taxableRows = [...charged.slice(0, basisCount), ...outer, ...charged.slice(basisCount)];

  // The tax base: every line above, which is what the engine charged GST on.
  // Summed from the rows rather than read from preTaxAmount, which includes
  // the reimbursements that GST is not charged on.
  const taxable = taxableRows.reduce((sum, row) => sum + n(row.amount), 0);

  const gst = findCharge(pricing.breakdown, 'gst');
  const roundOff = n(pricing.roundOffValue);

  return {
    taxableRows,
    taxable: Math.round(taxable * 100) / 100,
    gst: gst ? { ...line(gst), why: gstWhy(gst) } : null,
    reimbursements: reimbursementRows(pricing),
    roundOff: roundOff
      ? { key: 'roundOff', label: 'Round-off', amount: roundOff, why: 'to the next rupee' }
      : null,
    total: n(pricing.totalAmount),
  };
};

export default fareRows;
